// Une publication du fil des partenaires, telle que l'app l'affiche (thème
// sombre) : components/PublicationCard.js pour la carte (record, chiffres,
// ancien record barré, mini-graphique « même série »), puis
// components/CommentSection.js pour les réactions et les commentaires.
import React, { useEffect, useState } from "react";
import Apercu from "./Apercu";
import useApparition from "../useApparition";

// Réactions de l'app (CommentSection.js). active : celle que tu as donnée.
const REACTIONS = [
	{ emoji: "💪", total: 12, active: true },
	{ emoji: "❤️", total: 8 },
	{ emoji: "😳", total: 5 },
	{ emoji: "😂", total: 2 },
];

const COMMENTAIRES = [
	{ initiales: "HU", nom: "Hugo", texte: "Impressionnant" },
	{ initiales: "CH", nom: "Chloé", texte: "Wow la perf de fou" },
];

// Mini-graphique : même série, 5 dernières séances. Hauteurs comme
// computeBarHeights (base 24, +6 par progression, -6 par baisse, entre 14 et 36).
const BATONS = [24, 30, 30, 36, 36];

// Compte de 0 jusqu'au total une fois visible (directement le total en mouvement réduit).
function Compteur({ total, visible }) {
	const [valeur, setValeur] = useState(0);
	useEffect(() => {
		if (!visible) {
			setValeur(0);
			return undefined;
		}
		if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
			setValeur(total);
			return undefined;
		}
		let n = 0;
		const id = setInterval(() => {
			n += 1;
			setValeur(Math.min(n, total));
			if (n >= total) clearInterval(id);
		}, 90);
		return () => clearInterval(id);
	}, [visible, total]);
	return <span>{valeur}</span>;
}

export default function ApercuPublication({ className = "" }) {
	const [refReactions, visible] = useApparition(0.5);
	return (
		<Apercu
			className={className}
			description="Publication de Léa dans le fil des partenaires : nouveau record au squat, 5 × 102,5 kg à RPE 9, contre 5 × 100 kg, avec des réactions et les commentaires de Hugo et Chloé."
		>
			<div className="gs-pub">
				<div className="gs-pub-tete">
					<span className="gs-pub-avatar">LÉ</span>
					<div className="gs-pub-nom">
						<b>Léa</b>
						<span>Il y a 2 h</span>
					</div>
					<span className="gs-pub-record">
						<svg width="11" height="11" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
							<path d="M7 3h10v2h3v3a4 4 0 0 1-4 4h-.3A5 5 0 0 1 13 14.9V18h3v3H8v-3h3v-3.1A5 5 0 0 1 8.3 12H8a4 4 0 0 1-4-4V5h3z" />
						</svg>
						RECORD
					</span>
				</div>

				<div className="gs-pub-exo">Squat · Série 3</div>
				<div className="gs-pub-pointilles" />

				<div className="gs-pub-chiffres">
					<strong>5</strong>
					<i>×</i>
					<strong>102,5</strong>
					<span> kg · RPE 9</span>
				</div>
				<div className="gs-pub-avant">
					<s>5 × 100 kg</s>
					<span className="gs-pub-date">il y a 12 j</span>
					<span className="gs-pub-delta">+2,5 kg</span>
				</div>

				<div className="gs-pub-pointilles" />

				<div className="gs-pub-graphique">
					<span>MÊME SÉRIE · 5 DERNIÈRES SÉANCES</span>
					<svg width={BATONS.length * 11 - 3} height="40" aria-hidden="true">
						{BATONS.map((h, i) => (
							<rect
								key={i}
								x={i * 11}
								y={40 - h}
								width="8"
								height={h}
								rx="3"
								fill={i === BATONS.length - 1 ? "#30a49a" : "rgba(227,231,249,.19)"}
							/>
						))}
					</svg>
				</div>

				<div className="gs-pub-filet" />

				<div className="gs-pub-barre" ref={refReactions}>
					{REACTIONS.map((r) => (
						<span key={r.emoji} className={`gs-pub-reaction${r.active ? " gs-pub-reaction--active" : ""}`}>
							<span>{r.emoji}</span>
							<b>
								<Compteur total={r.total} visible={visible} />
							</b>
						</span>
					))}
					<span className="gs-pub-espace" />
					<span className="gs-pub-reaction gs-pub-reaction--active">
						<svg width="13" height="13" viewBox="0 0 24 24" fill="#30a49a" aria-hidden="true">
							<path d="M12 3C6.5 3 2 6.8 2 11.5c0 2.4 1.2 4.6 3.1 6.1L4.5 21l4-1.8c1.1.3 2.3.5 3.5.5 5.5 0 10-3.8 10-8.5S17.5 3 12 3z" />
						</svg>
						<b>{COMMENTAIRES.length}</b>
					</span>
				</div>

				<div className="gs-pub-zone">
					<div className="gs-pub-commentaires">
						{COMMENTAIRES.map((c, i) => (
							<div key={c.nom} className="gs-pub-commentaire gs-a-etape" style={{ "--i": i + 2 }}>
								<span className="gs-pub-mini-avatar">{c.initiales}</span>
								<div className="gs-pub-bulle">
									<div className="gs-pub-bulle-haut">
										<b>{c.nom}</b>
										<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(227,231,249,.35)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
											<path d="M5 21V4M5 4h11l-2 4 2 4H5" />
										</svg>
									</div>
									<p>{c.texte}</p>
								</div>
							</div>
						))}
					</div>
					<div className="gs-pub-separateur" />
					<div className="gs-pub-saisie">
						<span className="gs-pub-champ">Commenter…</span>
						<span className="gs-pub-envoyer">
							<svg width="14" height="14" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
								<path d="M3 20.5 21 12 3 3.5l2.5 7L15 12l-9.5 1.5z" />
							</svg>
						</span>
					</div>
				</div>
			</div>
		</Apercu>
	);
}
